import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

export interface AccessibilityPrefs {
  hands_free: boolean;
  wake_word_enabled: boolean;
  continuous_mode: boolean;
  autism_mode: boolean;
  narration_mode: boolean;
  spacebar_ptt: boolean;
  tts_speed: number;
  require_confirm_destructive: boolean;
  preferred_voice_lang: string | null;
}

const DEFAULT_PREFS: AccessibilityPrefs = {
  hands_free: false,
  wake_word_enabled: false,
  continuous_mode: false,
  autism_mode: false,
  narration_mode: false,
  spacebar_ptt: true,
  tts_speed: 1.0,
  require_confirm_destructive: true,
  preferred_voice_lang: null,
};

interface AccessibilityModeContextValue {
  prefs: AccessibilityPrefs;
  loading: boolean;
  updatePref: <K extends keyof AccessibilityPrefs>(key: K, value: AccessibilityPrefs[K]) => Promise<void>;
  reload: () => Promise<void>;
}

const AccessibilityModeContext = createContext<AccessibilityModeContextValue | null>(null);

export const AccessibilityModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useUser();
  const [prefs, setPrefs] = useState<AccessibilityPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!user?.id) {
      setPrefs(DEFAULT_PREFS);
      return;
    }
    setLoading(true);
    try {
      const { data } = await supabase
        .from('user_accessibility_prefs')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data) {
        setPrefs({
          hands_free: !!data.hands_free,
          wake_word_enabled: !!data.wake_word_enabled,
          continuous_mode: !!data.continuous_mode,
          autism_mode: !!data.autism_mode,
          narration_mode: !!data.narration_mode,
          spacebar_ptt: data.spacebar_ptt ?? true,
          tts_speed: Number(data.tts_speed) || 1.0,
          require_confirm_destructive: data.require_confirm_destructive ?? true,
          preferred_voice_lang: data.preferred_voice_lang ?? null,
        });
      } else {
        setPrefs(DEFAULT_PREFS);
      }
    } catch (err) {
      console.warn('[AccessibilityMode] load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const updatePref = useCallback(async <K extends keyof AccessibilityPrefs>(key: K, value: AccessibilityPrefs[K]) => {
    if (!user?.id) return;
    setPrefs(prev => ({ ...prev, [key]: value }));
    try {
      await supabase
        .from('user_accessibility_prefs')
        .upsert({ user_id: user.id, [key]: value as any } as never, { onConflict: 'user_id' });
    } catch (err) {
      console.warn('[AccessibilityMode] update failed:', err);
    }
  }, [user?.id]);

  return (
    <AccessibilityModeContext.Provider value={{ prefs, loading, updatePref, reload }}>
      {children}
    </AccessibilityModeContext.Provider>
  );
};

export const useAccessibilityMode = (): AccessibilityModeContextValue => {
  const ctx = useContext(AccessibilityModeContext);
  if (!ctx) {
    // Soft fallback so components can render outside provider during dev
    return {
      prefs: DEFAULT_PREFS,
      loading: false,
      updatePref: async () => {},
      reload: async () => {},
    };
  }
  return ctx;
};
